import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useState } from "react";
import { axiosInstance } from "../../../../utils/networking";
import { BroadcastFilter, ContentData } from "../broadcastTypes";
import { BROADCAST_TYPES } from "../broadcastTypes";

interface ReviewAndSendProps {
  broadcastType: string;
  contentData: ContentData;
  filter: BroadcastFilter;
  recipientCount: number | null;
  onSendSuccess: () => void;
}

const filterSummary = (filter: BroadcastFilter): string => {
  const parts: string[] = [];
  if (filter.bfc) parts.push(`BFC: ${filter.bfc}`);
  if (filter.city?.length) parts.push(`Cities: ${filter.city.join(", ")}`);
  if (filter.bussType?.length) parts.push(`Business Types: ${filter.bussType.join(", ")}`);
  if (filter.classification?.length) parts.push(`Classification: ${filter.classification.join(", ")}`);
  if (filter.certification?.length) parts.push(`Certification: ${filter.certification.join(", ")}`);
  return parts.length > 0 ? parts.join(" | ") : "All firms (no filter applied)";
};

const ReviewAndSend = ({
  broadcastType,
  contentData,
  filter,
  recipientCount,
  onSendSuccess,
}: ReviewAndSendProps) => {
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeConfig = BROADCAST_TYPES.find((t) => t.id === broadcastType);
  const count = recipientCount ?? 0;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      await axiosInstance.post("/broadcast/send", {
        broadcastType,
        subject: contentData.subject,
        htmlContent: contentData.htmlContent,
        eventId: contentData.eventId || null,
        recipientFilter: filter,
      });
      onSendSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || "An error occurred. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Review before sending
      </Typography>

      <List dense sx={{ bgcolor: "#f9f9f9", borderRadius: 2, border: "1px solid #e0e0e0" }}>
        <ListItem>
          <ListItemText
            primary="Broadcast Type"
            secondary={typeConfig?.label || broadcastType}
          />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="Subject" secondary={contentData.subject || "(no subject)"} />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="Recipients Filter" secondary={filterSummary(filter)} />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText
            primary="Estimated Recipients"
            secondary={
              recipientCount !== null
                ? `${count.toLocaleString()} recipient${count !== 1 ? "s" : ""}`
                : "Not yet previewed — go back to Step 3 to preview"
            }
          />
        </ListItem>
      </List>

      {recipientCount === 0 && (
        <Alert severity="warning">
          No recipients match the current filter. Go back and adjust your targeting criteria.
        </Alert>
      )}

      {recipientCount !== null && recipientCount > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{ color: "#9d2362", "&.Mui-checked": { color: "#9d2362" } }}
            />
          }
          label={
            <Typography variant="body2">
              I confirm I want to send this broadcast to{" "}
              <strong>{count.toLocaleString()} recipient{count !== 1 ? "s" : ""}</strong>. This action cannot be undone.
            </Typography>
          }
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={!confirmed || sending || !recipientCount}
          sx={{
            backgroundColor: "#9d2362",
            "&:hover": { backgroundColor: "#7a1a4d" },
            "&.Mui-disabled": { backgroundColor: "#e0e0e0" },
          }}
        >
          {sending ? "Sending…" : "Send Broadcast"}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewAndSend;
